import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PromotionBanner = ({ promotion, onClaim, onShare }) => {
  const getDiscountDisplay = () => {
    if (promotion?.discountType === 'percentage') {
      return `${promotion?.discountValue}% OFF`;
    } else if (promotion?.discountType === 'fixed') {
      return `R$ ${promotion?.discountValue?.toFixed(2)} OFF`;
    } else if (promotion?.discountType === 'bogo') {
      return 'COMPRE 1 LEVE 2';
    }
    return promotion?.discountValue;
  };

  const calculateTimeLeft = () => {
    const now = new Date();
    const endDate = new Date(promotion.endDate);
    const timeDiff = endDate - now;
    
    if (timeDiff <= 0) return null;
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} dia${days > 1 ? 's' : ''} restante${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hora${hours > 1 ? 's' : ''} restante${hours > 1 ? 's' : ''}`;
    } else {
      return 'Últimas horas!';
    }
  };

  const timeLeft = calculateTimeLeft();
  const isExpired = !timeLeft;

  return (
    <div className={`relative overflow-hidden rounded-lg shadow-warm hover:shadow-warm-lg transition-all duration-300 ${
      promotion?.featured ? 'ring-2 ring-accent' : ''
    }`}>
      <div className="relative h-64 md:h-80">
        <Image
          src={promotion?.bannerImage}
          alt={promotion?.title}
          className="w-full h-full object-cover"
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Discount badge */}
        <div className="absolute top-4 left-4">
          <div className="bg-accent text-accent-foreground px-4 py-2 rounded-full font-bold text-lg shadow-lg">
            {getDiscountDisplay()}
          </div>
        </div>

        {/* Featured badge */}
        {promotion?.featured && (
          <div className="absolute top-4 right-4">
            <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
              <Icon name="Star" size={14} />
              <span>Destaque</span>
            </div>
          </div>
        )}

        {/* Time left indicator */}
        {timeLeft && (
          <div className="absolute top-16 left-4">
            <div className="bg-warning text-warning-foreground px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>{timeLeft}</span>
            </div>
          </div>
        )}

        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-2xl font-heading font-bold mb-2 line-clamp-2">
                {promotion?.title}
              </h3>
              <p className="text-white/90 text-sm mb-3 line-clamp-2">
                {promotion?.description}
              </p>
              
              <div className="flex items-center space-x-4 text-sm text-white/80">
                <div className="flex items-center space-x-1">
                  <Icon name="Store" size={14} />
                  <span>{promotion?.storeName}</span>
                </div>
                {promotion?.category && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Tag" size={14} />
                    <span>{promotion?.category}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => onShare(promotion)}
              className="ml-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors duration-200"
              aria-label="Compartilhar promoção"
            >
              <Icon name="Share2" size={16} className="text-white" />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {promotion?.code && (
                <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg">
                  <span className="text-xs font-medium">Código: {promotion?.code}</span>
                </div>
              )}
              
              {promotion?.minPurchase && (
                <div className="text-xs text-white/80">
                  Compra mín. R$ {promotion?.minPurchase?.toFixed(2)}
                </div>
              )}
            </div>

            <Button
              variant={isExpired ? "secondary" : "default"}
              size="sm"
              onClick={() => onClaim(promotion)}
              disabled={isExpired}
              className="bg-white text-black hover:bg-white/90"
            >
              {isExpired ? 'Expirada' : 'Resgatar'}
            </Button>
          </div>
        </div>
      </div>
      {/* Terms and conditions */}
      {promotion?.terms && (
        <div className="p-4 bg-muted/50">
          <button className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
            <Icon name="Info" size={14} />
            <span>Ver termos e condições</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PromotionBanner;